import React, { PropsWithChildren } from 'react';
import { v4 as uuidv4 } from 'uuid';

type InputNavigationContextValue = {
  dataAttribute: Record<string, string>;
  focusNextInput: (target: HTMLInputElement) => void;
  focusPreviousInput: (target: HTMLInputElement) => void;
};

export const dataAttributeName = 'data-input-navigation';

export const focusNextInput = (target: HTMLInputElement, getInputs) => {
  const allInputs = getInputs();
  let index = allInputs.findIndex(input => input === target);
  if (index === -1) {
    return;
  }
  // focus the next enabled input
  while (index < allInputs.length - 1) {
    index += 1;
    const input = allInputs[index];
    if (!input.disabled) {
      input.focus();
      return;
    }
  }
};

export const focusPreviousInput = (target: HTMLInputElement, getInputs) => {
  const allInputs = getInputs();
  let index = allInputs.findIndex(input => input === target);
  if (index === -1) {
    return;
  }
  // focus the previous enabled input
  while (index > 0) {
    index -= 1;
    const input = allInputs[index];
    if (!input.disabled) {
      input.focus();
      return;
    }
  }
};

const InputNavigationContext = React.createContext<InputNavigationContextValue>(null);

const InputNavigationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const uniqueId = React.useMemo(() => uuidv4(), []);

  const dataAttribute = React.useMemo(
    () => ({
      [dataAttributeName]: uniqueId,
    }),
    [uniqueId],
  );

  const getInputs = React.useCallback(() => {
    return Array.from(
      document.querySelectorAll(`[${dataAttributeName}="${uniqueId}"]`),
    ) as HTMLInputElement[];
  }, [uniqueId]);

  const _focusNextInput = React.useCallback(
    (target: HTMLInputElement) => focusNextInput(target, getInputs),
    [getInputs],
  );

  const _focusPreviousInput = React.useCallback(
    (target: HTMLInputElement) => focusPreviousInput(target, getInputs),
    [getInputs],
  );

  const contextValue = React.useMemo<InputNavigationContextValue>(
    () => ({
      dataAttribute,
      focusNextInput: _focusNextInput,
      focusPreviousInput: _focusPreviousInput,
    }),
    [_focusNextInput, _focusPreviousInput, dataAttribute],
  );

  return (
    <InputNavigationContext.Provider value={contextValue}>
      {children}
    </InputNavigationContext.Provider>
  );
};

const useInputNavigationContext = () => {
  const context = React.useContext(InputNavigationContext);

  return context;
};

export { InputNavigationProvider, useInputNavigationContext };
